import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
} from "@mui/material";
import PATCloseButton from "..//Buttons/PATCloseButton";
import PATSaveButton from "../Buttons/PATSaveButton";
import { ImageTypes } from "../../utils/enums";
import { useDispatch } from "react-redux";
import { notify } from "../../reducers/NotifierReducer";
import { allowedTypes } from "../../utils/constants";

const PhotoDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  allPhotos = [],
}) => {
  const dispatch = useDispatch();

  const [isThumbnail, setIsThumbnail] = useState(false);
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [showThumbnailWarning, setShowThumbnailWarning] = useState(false);

  const resetState = () => {
    setIsThumbnail(false);
    setNotes("");
    setImageFile(null);
    setFileName("");
    setPreviewUrl("");
  };

  useEffect(() => {
    if (initialData) {
      setIsThumbnail(initialData.isThumbnail || false);
      setNotes(initialData.notes || "");
      setFileName(initialData.fileName || "");
      setImageFile(null);
      setPreviewUrl(
        initialData.imageData ? getImagePreview(initialData.imageData) : ""
      );
    } else {
      resetState();
    }
  }, [initialData]);

  useEffect(() => {
    const otherThumbnailExists =
      isThumbnail &&
      allPhotos?.some(
        (p) =>
          p.isThumbnail && (!initialData || p.imageID !== initialData.imageID)
      );
    setShowThumbnailWarning(otherThumbnailExists);
  }, [isThumbnail, allPhotos, initialData]);

  const getImagePreview = (imageData) => {
    if (!imageData) return "";
    try {
      const base64 =
        typeof imageData === "string"
          ? imageData
          : btoa(
              new Uint8Array(imageData.data || imageData).reduce(
                (data, byte) => data + String.fromCharCode(byte),
                ""
              )
            );
      return `data:image/jpeg;base64,${base64}`;
    } catch (error) {
      console.error("Error converting image data:", error);
      return "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      dispatch(notify("Only JPG, JPEG, and PNG files are allowed.", "error"));
      e.target.value = "";
      return;
    }
    setImageFile(file);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    const isEditing = !!initialData;
    const isCreating = !isEditing;

    if (isCreating && !imageFile) {
      dispatch(notify("Please select an image before saving.", "error"));
      return;
    }

    const photoData = {
      isThumbnail,
      notes: notes || null,
      fileName,
      imageData: imageFile,
      isImagePath: null,
      imageType: ImageTypes.Binary,
    };
    onSave(photoData);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initialData ? "Edit Photo" : "Add Photo"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isThumbnail}
                onChange={(e) => setIsThumbnail(e.target.checked)}
              />
            }
            label="Is Thumbnail?"
          />

          {showThumbnailWarning && (
            <Typography variant="body2" color="warning.main">
              Marking this as thumbnail will remove the existing image marked as
              thumbnail.
            </Typography>
          )}

          <TextField
            label="Notes"
            multiline
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <>
            <Button variant="outlined" component="label">
              Select Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Typography variant="body2">Selected File: {fileName}</Typography>
            )}
            {previewUrl && (
              <Box textAlign="center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: 200,
                    height: 200,
                    objectFit: "contain",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    marginTop: 8,
                  }}
                />
              </Box>
            )}
          </>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <PATSaveButton onClick={handleSubmit} tooltipText="Save" />
        <PATCloseButton onClick={handleClose} tooltipText="Close" />
      </DialogActions>
    </Dialog>
  );
};

export default PhotoDialog;
